import type { RatedTalentCategory } from '@/../../shared/types/talent-map';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ChevronDown, Search, ShieldAlert } from 'lucide-react';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { VersionTimeline, type TalentSnapshot } from '@/components/performance/talent-map/VersionTimeline';
import { HistorySummaryPanel } from '@/components/performance/talent-map/HistorySummaryPanel';
import { RATED_CATEGORIES, CATEGORY_CONFIG, getInitials, getAvatarColor, formatPeriodLabel } from '@/components/performance/talent-map/talentMapShared';
import {
  useTalentMapTeam, useTalentMapEvaluation, useTalentMapAllForPeople, useCycleConfig, useHrbpOverride,
  useTalentMapAccess,
} from '@/hooks/usePerformance';
import { useAuth } from '@/contexts/SharedAuthContext';
import { cn } from '@/lib/utils';
import { pendingLeaderChange, snapshotCategoryChanges, type TalentCategoryChange } from '@/../../shared/talent-map-rules';
import { toDMY } from '@/../../shared/dates';
import type { TalentCategory } from '@/components/performance/talent-map/talentMapShared';

interface PersonChanges {
  email: string;
  name: string;
  changes: TalentCategoryChange[];
}

/** Propuesta dicha en el chat sobre una evaluación cerrada, sin confirmar con el botón. */
interface PendingProposal {
  email: string;
  name: string;
  current: string;
  proposed: string;
  leader: string;
  at: string | null;
}

interface PersonOption {
  email: string;
  name: string;
  lastName: string;
  roleLine: string;
  hasRecord: boolean;
}

export function TalentMapHistoryTab() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { email: encodedEmail } = useParams<{ email?: string }>();
  const selectedEmail = encodedEmail ? decodeURIComponent(encodedEmail) : '';
  const { user, isAdmin } = useAuth();
  const myEmail = user?.email ?? '';
  const { data: tmAccess } = useTalentMapAccess();
  // Config-driven: base completa (People/HRBP/Admin) vs. edición de categoría (HRBP/Admin).
  const isPeopleAdmin = tmAccess?.canViewAll ?? false;
  const canEditCategory = tmAccess?.canEditCategory ?? false;

  const { data: cycle } = useCycleConfig();
  const { data: team, isLoading: loadingTeam } = useTalentMapTeam();
  const { data: allForPeople, isLoading: loadingAll } = useTalentMapAllForPeople(isPeopleAdmin);
  // El org scope trae también filas sintéticas (activos sin record, para el
  // mapa/distribución); el histórico solo tiene sentido sobre records reales.
  const allRecords = useMemo(() => (allForPeople ?? []).filter(r => !r.synthetic), [allForPeople]);

  const isMyReport = !!team?.some(m => m.email === selectedEmail);
  const { data: evalData, isLoading: loadingEval } = useTalentMapEvaluation(isMyReport ? selectedEmail : '');

  const [personFilter, setPersonFilter] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [overrideOpen, setOverrideOpen] = useState(false);
  // Solo categorías evaluables: 'onboarding' no se confirma ni se sobreescribe (es el z.enum del router).
  const [overrideCategory, setOverrideCategory] = useState<RatedTalentCategory | null>(null);
  const [overrideJustification, setOverrideJustification] = useState('');
  const hrbpOverride = useHrbpOverride();

  // Selector options: my team + (People) every evaluated person in the org
  const options: PersonOption[] = useMemo(() => {
    const map = new Map<string, PersonOption>();
    for (const m of team ?? []) {
      map.set(m.email, {
        email: m.email,
        name: m.name,
        lastName: m.lastName,
        roleLine: [m.role, m.area].filter(Boolean).join(' · ') || m.impactLevel,
        hasRecord: (m.snapshots?.length ?? 0) > 0,
      });
    }
    for (const r of allRecords ?? []) {
      if (!map.has(r.employeeEmail)) {
        map.set(r.employeeEmail, {
          email: r.employeeEmail,
          name: r.employeeName,
          lastName: r.employeeLastName,
          roleLine: [r.employeeRole, r.employeeArea].filter(Boolean).join(' · ') || r.impactLevel,
          hasRecord: (r.snapshots?.length ?? 0) > 0,
        });
      }
    }
    return Array.from(map.values()).sort((a, b) => `${a.name} ${a.lastName}`.localeCompare(`${b.name} ${b.lastName}`));
  }, [team, allRecords]);

  // Resolve the record to display
  const resolved = useMemo(() => {
    if (!selectedEmail) return null;
    if (isMyReport && evalData) {
      return {
        person: {
          email: selectedEmail,
          name: evalData.employee.name,
          lastName: evalData.employee.lastName,
          roleLine: [evalData.employee.role, evalData.employee.area].filter(Boolean).join(' · ') || evalData.employee.impactLevel,
        },
        record: evalData.record,
        leaderEmail: evalData.leaderEmail ?? myEmail,
      };
    }
    const candidates = (allRecords ?? []).filter(r => r.employeeEmail === selectedEmail);
    if (candidates.length === 0) {
      const opt = options.find(o => o.email === selectedEmail);
      return opt ? { person: opt, record: null, leaderEmail: null } : null;
    }
    const latest = [...candidates].sort((a, b) => (b.updatedAt ?? '').localeCompare(a.updatedAt ?? ''))[0];
    return {
      person: {
        email: selectedEmail,
        name: latest.employeeName,
        lastName: latest.employeeLastName,
        roleLine: [latest.employeeRole, latest.employeeArea].filter(Boolean).join(' · ') || latest.impactLevel,
      },
      record: latest,
      leaderEmail: latest.leaderEmail,
    };
  }, [selectedEmail, isMyReport, evalData, allRecords, options, myEmail]);

  // Cambios de categoría del ciclo (Sol 25/09): por persona, solo nombre y de
  // qué a qué categoría, después de la primera confirmación. People ve toda la
  // base; un líder, su equipo. Ordenado por el cambio más reciente.
  const categoryChanges: PersonChanges[] = useMemo(() => {
    const src: Array<{ email: string; name: string; snapshots: unknown }> = isPeopleAdmin
      ? (allRecords ?? []).map(r => ({ email: r.employeeEmail, name: `${r.employeeName} ${r.employeeLastName}`.trim(), snapshots: r.snapshots }))
      : (team ?? []).map(m => ({ email: m.email, name: `${m.name} ${m.lastName}`.trim(), snapshots: m.snapshots }));
    return src
      .map(p => ({ email: p.email, name: p.name, changes: snapshotCategoryChanges(p.snapshots as TalentSnapshot[]) }))
      .filter(p => p.changes.length > 0)
      .sort((a, b) => b.changes[b.changes.length - 1].at.localeCompare(a.changes[a.changes.length - 1].at));
  }, [isPeopleAdmin, allRecords, team]);
  // Propuestas en el chat SIN confirmar (Euge 25/09: "otro par de modificaciones
  // que sé que estuvieron haciendo" no salían en el Histórico porque nunca se
  // confirmaron). Misma regla que la card de la evaluación (pendingLeaderChange).
  const pendingProposals: PendingProposal[] = useMemo(() => {
    const src: Array<{ email: string; name: string; leader: string; record: Parameters<typeof pendingLeaderChange>[0] & { leaderProposedAt?: string | null } }> = isPeopleAdmin
      ? (allRecords ?? []).map(r => ({ email: r.employeeEmail, name: `${r.employeeName} ${r.employeeLastName}`.trim(), leader: r.leaderEmail, record: r }))
      : (team ?? []).map(m => ({ email: m.email, name: `${m.name} ${m.lastName}`.trim(), leader: m.leaderEmail ?? myEmail, record: m }));
    return src
      .flatMap(p => {
        const proposed = pendingLeaderChange(p.record);
        return proposed && p.record?.finalCategory
          ? [{ email: p.email, name: p.name, current: p.record.finalCategory, proposed, leader: p.leader, at: p.record.leaderProposedAt ?? null }]
          : [];
      })
      .sort((a, b) => (b.at ?? '').localeCompare(a.at ?? '') || a.name.localeCompare(b.name, 'es'));
  }, [isPeopleAdmin, allRecords, team, myEmail]);

  const categoryLabel = (c: string) => {
    const cfg = CATEGORY_CONFIG[c as TalentCategory];
    return cfg ? t(cfg.labelKey) : c;
  };
  const categoryChip = (c: string) => CATEGORY_CONFIG[c as TalentCategory]?.chip ?? 'bg-muted text-foreground';
  const changeBy = (source: string) =>
    t(source === 'hrbp' ? 'performance.talent_map.history_tab.changes_by_hrbp' : source === 'system' ? 'performance.talent_map.history_tab.changes_by_system' : 'performance.talent_map.history_tab.changes_by_leader');

  const snapshots: TalentSnapshot[] = (resolved?.record?.snapshots ?? []) as TalentSnapshot[];
  const record = resolved?.record;
  const detailPending = pendingLeaderChange(record as Parameters<typeof pendingLeaderChange>[0]);
  const canOverride = canEditCategory && !!record && !!resolved?.leaderEmail
    && (resolved.leaderEmail !== myEmail || isAdmin);

  const isLoading = loadingTeam || (isPeopleAdmin && loadingAll) || (isMyReport && loadingEval);

  function selectPerson(email: string) {
    setPickerOpen(false);
    navigate(`/performance/talent-map/history/${encodeURIComponent(email)}`);
  }

  async function handleOverride() {
    if (!overrideCategory || !resolved?.leaderEmail) return;
    await hrbpOverride.mutateAsync({
      leaderEmail: resolved.leaderEmail,
      employeeEmail: selectedEmail,
      newCategory: overrideCategory,
      justification: overrideJustification.trim(),
    });
    setOverrideOpen(false);
    setOverrideCategory(null);
    setOverrideJustification('');
  }

  if (isLoading && !resolved) {
    return <div className="flex items-center justify-center h-64"><WinclapLoader size={48} className="text-foreground/25" /></div>;
  }

  const filteredOptions = personFilter
    ? options.filter(o => `${o.name} ${o.lastName} ${o.email}`.toLowerCase().includes(personFilter.toLowerCase()))
    : options;

  const personName = resolved ? `${resolved.person.name} ${resolved.person.lastName}` : '';

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-6 items-start">
      {/* ─── Center panel ─── */}
      <div className="space-y-4 min-w-0">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t('performance.talent_map.history_tab.title')}</h1>
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-teal-600 mt-1">
            {t('performance.talent_map.people_period', { period: formatPeriodLabel(cycle) })}
          </p>
        </div>

        {/* Person selector card */}
        <Card>
          <CardContent className="py-3.5 flex items-center justify-between gap-3">
            {resolved ? (
              <div className="flex items-center gap-3 min-w-0">
                <div
                  className="flex h-10 w-10 items-center justify-center rounded-full text-sm font-bold shrink-0 text-gray-700"
                  style={{ backgroundColor: getAvatarColor(personName) }}
                >
                  {getInitials(resolved.person.name, resolved.person.lastName)}
                </div>
                <div className="min-w-0">
                  <p className="font-bold truncate">{personName}</p>
                  <p className="text-sm text-teal-600 truncate">{resolved.person.roleLine}</p>
                  {resolved.leaderEmail && resolved.leaderEmail !== myEmail && (
                    <p className="text-xs text-muted-foreground truncate">
                      {t('performance.talent_map.team_tab.evaluated_by', { name: resolved.leaderEmail.split('@')[0] })}
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">{t('performance.talent_map.history_tab.pick_person')}</p>
            )}
            <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="gap-1.5 shrink-0">
                  {t('performance.talent_map.history_tab.change_person')}
                  <ChevronDown className="h-3.5 w-3.5" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-72 p-2">
                <div className="relative mb-2">
                  <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                  <Input
                    value={personFilter}
                    onChange={e => setPersonFilter(e.target.value)}
                    placeholder={t('performance.talent_map.team_tab.search_placeholder')}
                    className="pl-8 h-8 text-sm"
                  />
                </div>
                <div className="max-h-64 overflow-y-auto space-y-0.5">
                  {filteredOptions.length === 0 && (
                    <p className="text-xs text-muted-foreground p-2">{t('performance.talent_map.no_results')}</p>
                  )}
                  {filteredOptions.map(o => (
                    <button
                      key={o.email}
                      onClick={() => selectPerson(o.email)}
                      className={cn(
                        'w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-left hover:bg-accent transition-colors',
                        o.email === selectedEmail && 'bg-accent',
                      )}
                    >
                      <div
                        className="flex h-7 w-7 items-center justify-center rounded-full text-[10px] font-bold shrink-0 text-gray-700"
                        style={{ backgroundColor: getAvatarColor(`${o.name} ${o.lastName}`) }}
                      >
                        {getInitials(o.name, o.lastName)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate">{o.name} {o.lastName}</p>
                        <p className="text-[11px] text-muted-foreground truncate">{o.roleLine}</p>
                      </div>
                      {!o.hasRecord && (
                        <span className="text-[10px] text-muted-foreground shrink-0">{t('performance.talent_map.history_tab.no_versions_short')}</span>
                      )}
                    </button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </CardContent>
        </Card>

        {/* Sin persona elegida: lista compacta de cambios de categoría (Sol 25/09) */}
        {!resolved ? (
          <Card>
            <CardContent className="p-5">
              <div className="flex items-baseline justify-between gap-3 mb-1">
                <h3 className="font-semibold">{t('performance.talent_map.history_tab.changes_title')}</h3>
                {/* Cuenta CAMBIOS, no personas (frontend-auditor 25/09): una persona puede tener varios. */}
                <span className="text-2xl font-bold tabular-nums text-foreground/80">{categoryChanges.reduce((n, p) => n + p.changes.length, 0)}</span>
              </div>
              <p className="text-xs text-muted-foreground mb-3">{t('performance.talent_map.history_tab.changes_subtitle')}</p>
              {categoryChanges.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t('performance.talent_map.history_tab.changes_empty')}</p>
              ) : (
                <ul className="divide-y">
                  {categoryChanges.map(p => (
                    <li key={p.email} className="py-2">
                      <button type="button" onClick={() => selectPerson(p.email)} className="w-full text-left flex flex-wrap items-baseline gap-x-3 gap-y-1 hover:opacity-80">
                        <span className="font-medium text-sm">{p.name}</span>
                        <span className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                          {p.changes.map(c => (
                            <span key={c.versionNumber} className="inline-flex items-center gap-1.5">
                              <span className={cn('rounded-full px-2 py-0.5 text-xs font-medium', categoryChip(c.from))}>{categoryLabel(c.from)}</span>
                              <span className="text-muted-foreground">→</span>
                              <span className={cn('rounded-full px-2 py-0.5 text-xs font-medium', categoryChip(c.to))}>{categoryLabel(c.to)}</span>
                              <span className="text-xs text-muted-foreground">{toDMY(c.at.slice(0, 10))} · {changeBy(c.source)}</span>
                            </span>
                          ))}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              {/* Propuestas en el chat sin confirmar (Euge 25/09) */}
              <div className="mt-6 flex items-baseline justify-between gap-3 mb-1">
                <h3 className="font-semibold">{t('performance.talent_map.history_tab.pending_title')}</h3>
                <span className={cn('text-2xl font-bold tabular-nums', pendingProposals.length > 0 ? 'text-amber-600' : 'text-foreground/80')}>{pendingProposals.length}</span>
              </div>
              <p className="text-xs text-muted-foreground mb-3">{t('performance.talent_map.history_tab.pending_subtitle')}</p>
              {pendingProposals.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t('performance.talent_map.history_tab.pending_empty')}</p>
              ) : (
                <ul className="divide-y">
                  {pendingProposals.map(p => (
                    <li key={p.email} className="py-2">
                      <button type="button" onClick={() => selectPerson(p.email)} className="w-full text-left flex flex-wrap items-baseline gap-x-3 gap-y-1 hover:opacity-80">
                        <span className="font-medium text-sm">{p.name}</span>
                        <span className="inline-flex flex-wrap items-center gap-1.5 text-sm">
                          <span className={cn('rounded-full px-2 py-0.5 text-xs font-medium', categoryChip(p.current))}>{categoryLabel(p.current)}</span>
                          <span className="text-muted-foreground">→</span>
                          <span className={cn('rounded-full px-2 py-0.5 text-xs font-medium', categoryChip(p.proposed))}>{categoryLabel(p.proposed)}</span>
                          <span className="text-xs text-muted-foreground">
                            {t('performance.talent_map.history_tab.pending_by', { leader: p.leader.split('@')[0] })}{p.at ? ` · ${toDMY(p.at.slice(0, 10))}` : ''}
                          </span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              <p className="mt-4 text-xs text-muted-foreground text-center">{t('performance.talent_map.history_tab.pick_person')}</p>
            </CardContent>
          </Card>
        ) : snapshots.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center space-y-3">
              <p className="text-sm text-muted-foreground">{t('performance.talent_map.history_tab.no_versions')}</p>
              {isMyReport && ((evalData as any)?.canEvaluate ?? (evalData as any)?.isDirect) !== false && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/performance/talent-map/evaluation/${encodeURIComponent(selectedEmail)}`)}
                >
                  {t('performance.talent_map.history_tab.start_evaluation')}
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <VersionTimeline snapshots={snapshots} />
        )}

        {/* Propuesta en el chat sin confirmar (Euge 25/09): se ve en el detalle y,
            si puede, la aplica con el override ya preseleccionado. */}
        {detailPending && (
          <div className="rounded-xl bg-amber-50 border border-amber-200 dark:bg-amber-950/20 p-3 flex flex-wrap items-center justify-between gap-2 text-sm text-amber-800 dark:text-amber-300">
            <span>
              {t('performance.talent_map.history_tab.detail_pending', {
                proposed: categoryLabel(detailPending),
                current: record?.finalCategory ? categoryLabel(record.finalCategory) : '',
                leader: (resolved?.leaderEmail ?? '').split('@')[0],
              })}
            </span>
            {canOverride && (
              <Button variant="outline" size="sm" className="gap-2" onClick={() => { setOverrideCategory(detailPending as RatedTalentCategory); setOverrideOpen(true); }}>
                <ShieldAlert className="h-4 w-4" />
                {t('performance.talent_map.history_tab.apply_pending')}
              </Button>
            )}
          </div>
        )}

        {/* HRBP override (spec §6.3) */}
        {canOverride && (
          <>
            <Button variant="outline" size="sm" className="gap-2" onClick={() => setOverrideOpen(true)}>
              <ShieldAlert className="h-4 w-4" />
              {t('performance.talent_map.history_tab.hrbp_override_button')}
            </Button>
            <Dialog open={overrideOpen} onOpenChange={setOverrideOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{t('performance.talent_map.history_tab.hrbp_override_title', { name: personName })}</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    {RATED_CATEGORIES.map(cat => {
                      const c = CATEGORY_CONFIG[cat];
                      const chosen = overrideCategory === cat;
                      return (
                        <button
                          key={cat}
                          onClick={() => setOverrideCategory(cat)}
                          className={cn(
                            'rounded-xl border overflow-hidden text-left transition-all',
                            chosen ? `${c.ring} shadow-xs` : 'border-border hover:bg-accent',
                          )}
                        >
                          <div className={`h-1 w-full ${c.bar}`} />
                          <p className="p-2.5 text-xs font-bold">{t(c.labelKey)}</p>
                        </button>
                      );
                    })}
                  </div>
                  <Textarea
                    value={overrideJustification}
                    onChange={e => setOverrideJustification(e.target.value)}
                    placeholder={t('performance.talent_map.history_tab.hrbp_justification_placeholder')}
                    rows={3}
                    className="text-sm resize-none"
                  />
                </div>
                <DialogFooter>
                  <Button variant="outline" size="sm" onClick={() => setOverrideOpen(false)}>
                    {t('common.ui.cancel')}
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleOverride}
                    disabled={!overrideCategory || overrideJustification.trim().length < 10 || hrbpOverride.isPending}
                  >
                    {hrbpOverride.isPending ? <WinclapLoader size={14} /> : t('performance.talent_map.confirm_category')}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        )}
      </div>

      {/* ─── Right panel ─── */}
      {resolved && snapshots.length > 0 && (
        <div className="lg:sticky lg:top-4">
          <HistorySummaryPanel
            snapshots={snapshots}
            finalCategory={record?.finalCategory}
            processStatus={record?.processStatus}
            updatedAt={record?.updatedAt}
          />
        </div>
      )}
    </div>
  );
}
